import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import { MouseEvent, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';

/**
 * The grid of a timecard or expense row. The selection column is a fixed 44px and the content columns
 * split the rest proportionally, so the checkbox is never clipped when the pane is narrow (e.g. with the
 * Summary drawer open).
 */
const ROW_COLUMNS = {
    basic: 'grid-cols-[44px_minmax(0,24fr)_minmax(0,6fr)_minmax(0,5fr)]',
    timecardAdvanced: 'grid-cols-[44px_minmax(0,16fr)_minmax(0,6fr)_minmax(0,8fr)_minmax(0,5fr)]',
    expenseAdvanced: 'grid-cols-[44px_minmax(0,16fr)_minmax(0,7fr)_minmax(0,7fr)_minmax(0,5fr)]',
} as const;

/** A row whose item has a pending action (moved, credited, postponed, non-billable): frozen, its content faded. */
const FROZEN = 'pointer-events-none [&_.opacity-decreasing-when-disabled]:opacity-40';

/** A frozen row on a correction invoice, tinted in the correction's warning colour. */
export const FROZEN_CORRECTION = cn('bg-warning-light/50', FROZEN);

const ROW_STATE = {
    selected: 'bg-grey-50',
    new: 'bg-success-light',
    frozenCorrection: FROZEN_CORRECTION,
    frozen: cn('bg-grey-50', FROZEN),
} as const;

export type ItemRowState = keyof typeof ROW_STATE | null;

/** The new design's row tints: a ticked row in the primary tint, a new one in the success tint, a frozen one faded. */
const TABLE_ROW_STATE: Record<keyof typeof ROW_STATE, string> = {
    selected: 'bg-primary-tint',
    new: 'bg-success-tint',
    frozenCorrection: cn('bg-warning-tint', FROZEN),
    frozen: FROZEN,
};

/** A timecard or expense `<tr>` of the new design's item table. */
export const itemTableRowClassName = (state: ItemRowState) =>
    cn('border-divider border-b align-top', state && TABLE_ROW_STATE[state]);

export const getItemRowState = ({
    isSelected,
    isNew,
    isRowDisabled,
    isCorrectionInvoice,
    readonly,
}: {
    isSelected: boolean | undefined;
    isNew: boolean;
    isRowDisabled: boolean;
    isCorrectionInvoice: boolean;
    readonly: boolean;
}): ItemRowState => {
    if (isSelected) return 'selected';
    if (isNew) return 'new';
    if (isRowDisabled && isCorrectionInvoice) return 'frozenCorrection';
    if (isRowDisabled && !readonly) return 'frozen';
    return null;
};

export const itemRowClassName = (columns: keyof typeof ROW_COLUMNS, state: ItemRowState) =>
    cn('border-divider grid border-b', ROW_COLUMNS[columns], state && ROW_STATE[state]);

/** The selection column's cell. Read-only, it has no rule to its right. */
export const itemSelectionCellClassName = (readonly: boolean) => cn('h-full', !readonly && 'border-divider border-r');

/** A content cell of the row. */
export const ITEM_CELL = 'border-divider h-full border-r p-2';

/** The row's last cell, holding the amount at its foot. */
export const ITEM_TOTAL_CELL = 'bg-grey-50 flex h-full items-end p-2';

/** What the selection cell holds when the row cannot be selected. */
export const EmptySelection = () => <div className="min-w-4 pt-4" />;

/**
 * A click on the column outside the checkbox clicks the checkbox instead of letting the label activate it:
 * the label's native activation focuses the checkbox, and in a tall row whose checkbox is outside the
 * list's viewport that focus scrolls the list.
 */
const clickCheckboxWithoutFocus = (event: MouseEvent<HTMLLabelElement>) => {
    // Base UI's own checkbox click prevents default; its toggle then arrives as a click on its hidden input.
    if (event.isDefaultPrevented() || event.target instanceof HTMLInputElement) return;
    event.preventDefault();
    event.currentTarget.querySelector<HTMLElement>('[data-slot=checkbox]')?.click();
};

/**
 * A label around the row's checkbox, so a click anywhere in the column toggles it. Hovering the column
 * mirrors the checkbox's own hover, so the whole column reads as one control; `select-none` stops rapid
 * clicks from starting a text selection that spills into the neighbouring cell.
 */
export const SelectionLabel = ({ htmlFor, children }: { htmlFor: string; children: ReactNode }) => (
    <label
        htmlFor={htmlFor}
        onClick={clickCheckboxWithoutFocus}
        className="not-has-data-disabled:hover:bg-grey-100 [&:not(:has([data-disabled])):hover_[data-slot=checkbox]:not([data-checked])]:border-secondary-light flex h-full cursor-pointer items-center justify-center select-none has-data-disabled:cursor-default"
    >
        {children}
    </label>
);

/** The "Edit" button of an item row or a case header. */
export const ItemEditButton = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
    <Button
        {...buttonStyle({ variant: 'outlined', size: 'extraSmall' }, 'hover:border-divider hover:bg-grey-100')}
        onClick={onClick}
        disabled={disabled}
    >
        <FormattedMessage id="edit" />
    </Button>
);

/** The breakdown an item's amount tooltip shows, one step per row. */
export const CALCULATION = 'flex flex-col gap-2 p-2';

/**
 * One step of a breakdown: its terms side by side, each label over its value. The last step is
 * {@link CALCULATION_RESULT}, which has no rule under it.
 */
export const CALCULATION_STEP = 'border-divider flex flex-row items-center gap-2 border-b pb-2 *:flex *:flex-col';

/** A step whose every figure is bold, labels included. `!`: it beats the weight their own text classes set. */
export const CALCULATION_STEP_BOLD = cn(CALCULATION_STEP, '**:font-semibold!');

export const CALCULATION_RESULT = 'bg-grey-50 flex flex-row items-center gap-2 p-2 *:flex *:flex-col **:font-semibold!';
